import React, { useCallback, useMemo, useState } from 'react';
import { FeedInspectButton, FeedInspectUrlLink } from '../../components/FeedInspectActions';
import SourceAttribution from '../../components/investigation/SourceAttribution';
import MatchBadge from '../../components/investigation/MatchBadge';
import {
  FEED_CARD_SHELL,
  FEED_CHIP,
  FEED_META,
  FEED_TITLE,
} from '../../components/investigation/feedCardStyles';

/**
 * @typedef {{ host?: string, status?: string, resolved?: boolean|null } | string} SubdomainEntry
 */

/**
 * Normalize subdomain list from flat strings or annotated objects.
 *
 * @param {object} res
 * @returns {{ host: string, status: string, resolved: boolean|null }[]}
 */
function normalizeSubdomainEntries(res) {
  const annotated = res?.details?.subdomains_annotated;
  if (Array.isArray(annotated) && annotated.length) {
    return annotated
      .map((item) => {
        if (typeof item === 'string') {
          const host = String(item).trim().toLowerCase();
          return host ? { host, status: 'UNKNOWN', resolved: null } : null;
        }
        const host = String(item?.host || '').trim().toLowerCase();
        if (!host) return null;
        return {
          host,
          status: String(item?.status || 'UNKNOWN').toUpperCase(),
          resolved: item?.resolved ?? null,
        };
      })
      .filter(Boolean);
  }
  const flat = Array.isArray(res?.details?.subdomains) ? res.details.subdomains : [];
  return flat
    .map((s) => {
      const host = String(s).trim().toLowerCase();
      return host ? { host, status: 'UNKNOWN', resolved: null } : null;
    })
    .filter(Boolean);
}

/**
 * Domain infrastructure / Certificate Transparency card (crt.sh + OTX + RDAP).
 *
 * @param {{ res: object, title?: string }} props
 */
export default function CertificateTransparencyCard({ res, title }) {
  const [copied, setCopied] = useState(false);
  const safeTitle = String(title || res?.title || 'Domain infrastructure').trim();
  const platform = String(res?.platform || 'crt.sh');
  const entries = useMemo(() => normalizeSubdomainEntries(res), [res]);
  const hosts = useMemo(() => entries.map((e) => e.host), [entries]);

  const threatSignals = Number(
    res?.threat_signals ?? res?.details?.otx_pulse_count ?? 0,
  );
  const threatLabel =
    String(res?.details?.threat_label || '').trim() ||
    (threatSignals > 0 ? `${threatSignals} pulse(s)` : 'Clean');
  const otxClean = threatSignals <= 0 && !/malicious/i.test(String(res?.details?.otx_reputation || ''));

  const ageDays = res?.age_days ?? res?.details?.age_days;
  const ageLabel =
    String(res?.details?.age_label || '').trim() ||
    (typeof ageDays === 'number'
      ? ageDays >= 365
        ? `${Math.floor(ageDays / 365.25)} years`
        : `${ageDays} days`
      : null);

  const subdomainCount = Number(
    res?.subdomains_count ?? res?.details?.subdomain_count ?? hosts.length,
  );

  const copyAll = useCallback(async () => {
    if (!hosts.length) return;
    const text = hosts.join('\n');
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      setCopied(false);
    }
  }, [hosts]);

  return (
    <article
      className={FEED_CARD_SHELL}
      data-feed-result="true"
      data-testid="certificate-transparency-card"
    >
      <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-5">
        <div className="min-w-0 flex-1 space-y-2">
          <p className={`${FEED_META} text-cyan-300`}>
            <SourceAttribution res={res} fallback={platform} />
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <h3 className={FEED_TITLE}>{safeTitle}</h3>
            <MatchBadge value={res?.source_reliability ?? res?.score} />
          </div>

          <div
            className="flex flex-wrap items-center gap-1.5"
            data-testid="domain-infra-summary-badges"
          >
            <span
              className={`${FEED_CHIP} ${
                otxClean
                  ? 'border-emerald-500/35 text-emerald-200'
                  : 'border-rose-500/40 text-rose-200'
              }`}
            >
              OTX Threat: {otxClean ? 'Clean' : threatLabel}
            </span>
            {ageLabel ? (
              <span className={`${FEED_CHIP} border-sky-500/30 text-sky-200`}>
                Age: {ageLabel}
              </span>
            ) : null}
            <span className={FEED_CHIP}>Subdomains: {subdomainCount}</span>
            {res?.registrar || res?.details?.registrar ? (
              <span className={`${FEED_CHIP} border-slate-500/40 text-slate-300`}>
                {String(res?.registrar || res?.details?.registrar)}
              </span>
            ) : null}
          </div>

          {res?.snippet ? (
            <p className="text-xs leading-relaxed text-slate-400 line-clamp-2">{res.snippet}</p>
          ) : null}
          <FeedInspectUrlLink res={res} fallback="CT record" />

          {entries.length > 0 ? (
            <div className="space-y-1.5">
              <div className="flex items-center justify-between gap-2">
                <span className="text-[9px] font-black uppercase tracking-widest text-slate-500">
                  Subdomains ({entries.length})
                </span>
                <button
                  type="button"
                  onClick={copyAll}
                  className="rounded-md border border-cyan-500/35 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-cyan-200 transition hover:border-cyan-400/60 hover:bg-cyan-950/40"
                >
                  {copied ? 'Copied' : 'Copy All'}
                </button>
              </div>
              <div
                className="max-h-52 overflow-y-auto rounded-lg border border-slate-800/60 bg-slate-950/40 p-2.5 pr-2 font-mono text-xs"
                data-testid="ct-subdomains-scroll"
              >
                <ul className="m-0 list-none space-y-0.5 p-0">
                  {entries.map((entry) => (
                    <li
                      key={entry.host}
                      className="flex items-center justify-between gap-2 text-cyan-300"
                    >
                      <span className="min-w-0 truncate">{entry.host}</span>
                      {entry.status === 'ACTIVE' || entry.resolved === true ? (
                        <span className="shrink-0 rounded border border-emerald-500/40 px-1 py-px text-[9px] font-bold uppercase tracking-wide text-emerald-300">
                          ACTIVE
                        </span>
                      ) : entry.status === 'UNRESOLVED' || entry.resolved === false ? (
                        <span className="shrink-0 rounded border border-slate-600/50 px-1 py-px text-[9px] font-bold uppercase tracking-wide text-slate-500">
                          UNRESOLVED
                        </span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ) : null}
        </div>
        <FeedInspectButton res={res} className="min-h-[44px] w-full sm:w-auto sm:min-h-0" />
      </div>
    </article>
  );
}
